import { AlertCircle, CheckCircle2, Circle } from 'lucide-react';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';

/**
 * SIL-1198/SIL-1574: extracted verbatim from individual-profile.tsx (no
 * behavior change) so `PhoneField` (../components/PhoneField.tsx) can reuse
 * it outside that route. individual-profile.tsx's other fields (`TextField`)
 * keep using it via this import rather than a local definition — one
 * implementation, not two that drift.
 *
 * Required/optional/invalid/complete status marker for a form field. Status
 * is never color-alone: each state pairs an icon with the (always-present)
 * "Required" text, plus sr-only text distinguishing the muted/attention/
 * complete/invalid states for assistive tech.
 */
export function FieldStatusMarker({
  id,
  required,
  complete,
  invalid,
  attention,
}: {
  id: string;
  required: boolean;
  complete: boolean;
  invalid: boolean;
  attention: boolean;
}) {
  if (!required) {
    return (
      <span id={id}>
        <StatusPill tone="neutral">Optional</StatusPill>
      </span>
    );
  }
  if (invalid) {
    return (
      <span id={id}>
        <StatusPill tone="red" className="gap-1">
          <AlertCircle className="size-3 shrink-0" aria-hidden="true" />
          <span>Required</span>
          <span className="sr-only"> — invalid</span>
        </StatusPill>
      </span>
    );
  }
  if (complete) {
    return (
      <span id={id}>
        <StatusPill tone="green" className="gap-1">
          <CheckCircle2 className="size-3 shrink-0" aria-hidden="true" />
          <span>Required</span>
          <span className="sr-only"> — complete</span>
        </StatusPill>
      </span>
    );
  }
  return (
    <span id={id}>
      <StatusPill tone={attention ? 'amber' : 'neutral'} className="gap-1">
        <Circle className="size-3 shrink-0" aria-hidden="true" />
        <span>Required</span>
        <span className="sr-only">{attention ? ' — required, not yet filled' : ''}</span>
      </StatusPill>
    </span>
  );
}

/** Quiet per-field/per-section autosave indicator — text, not color alone. */
export function SavedIndicator({ saving, saved }: { saving?: boolean; saved?: boolean }) {
  if (saving) return <span className="text-2xs text-muted-foreground">Saving…</span>;
  if (saved) return <span className="text-2xs text-status-green">Saved</span>;
  return null;
}
